/**
 * Checklist Section Component
 */

import { useState, useEffect, useMemo } from 'react';
import { CheckCircle2, Circle, ClipboardList, Cpu, Image, PenLine, ExternalLink, Loader2, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import type { InstallationJob, ChecklistItem } from '../types';
import { getChecklistFileUrl } from '../api';
import { ImageViewerDialog } from './ImageViewerDialog';

interface ChecklistSectionProps {
  job: InstallationJob;
}

interface ImageViewerState {
  open: boolean;
  url: string | null;
  title: string;
  type: 'photo' | 'signature';
}

function ChecklistItemRow({
  item,
  fileUrl,
  loadingUrl,
  onViewImage,
}: {
  item: ChecklistItem;
  fileUrl: string | null;
  loadingUrl: boolean;
  onViewImage: (url: string, title: string, type: 'photo' | 'signature') => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const isCompleted = item.value !== undefined && item.value !== null && item.value !== '';

  const hasPhoto = item.type === 'photo' && item.photoFileId;
  const hasSignature = item.type === 'signature' && item.signatureFileId;
  const hasDetails = isCompleted && (item.completedAt || item.notes);

  const formatValue = () => {
    if (item.value === undefined || item.value === null) return null;
    switch (item.type) {
      case 'checkbox':
        return item.value ? 'Yes' : 'No';
      case 'date':
        return new Date(item.value as string).toLocaleDateString('en-CA');
      case 'photo':
      case 'signature':
        return null;
      default:
        return String(item.value);
    }
  };

  const handleViewImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (fileUrl) {
      onViewImage(fileUrl, item.displayName, item.type as 'photo' | 'signature');
    }
  };

  const formattedValue = formatValue();

  return (
    <div
      onClick={() => hasDetails && setExpanded(!expanded)}
      className={cn(
        'rounded-lg border p-3 transition-colors',
        isCompleted ? 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800' : '',
        hasDetails ? 'cursor-pointer' : '',
      )}
    >
      {/* Scan row: icon — label — value — thumbnails — chevron */}
      <div className="flex items-center gap-3">
        <div className={cn('shrink-0', isCompleted ? 'text-green-600' : 'text-muted-foreground')}>
          {isCompleted ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
        </div>

        <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
          <span className={cn('font-medium', isCompleted && 'text-green-800 dark:text-green-200')}>
            {item.displayName}
          </span>
          {isCompleted && formattedValue && (
            <>
              <span className="text-muted-foreground">—</span>
              <span className="font-medium">{formattedValue}</span>
            </>
          )}
          {item.required && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">Required</Badge>
          )}
        </div>

        {/* Inline photo thumbnail */}
        {hasPhoto && (
          <div className="shrink-0">
            {loadingUrl ? (
              <div className="w-10 h-10 rounded border flex items-center justify-center bg-muted">
                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
              </div>
            ) : fileUrl ? (
              <button
                onClick={handleViewImage}
                className="group relative block w-10 h-10 rounded overflow-hidden border hover:border-primary transition-colors"
              >
                <img src={fileUrl} alt={item.displayName} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <ExternalLink className="h-3 w-3 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                </div>
              </button>
            ) : (
              <div className="w-10 h-10 rounded border flex items-center justify-center bg-muted">
                <Image className="h-3 w-3 text-muted-foreground" />
              </div>
            )}
          </div>
        )}

        {/* Inline signature thumbnail */}
        {hasSignature && (
          <div className="shrink-0">
            {loadingUrl ? (
              <div className="w-16 h-8 rounded border flex items-center justify-center bg-white">
                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
              </div>
            ) : fileUrl ? (
              <button
                onClick={handleViewImage}
                className="group relative block w-16 h-8 rounded overflow-hidden border bg-white hover:border-primary transition-colors"
              >
                <img src={fileUrl} alt="Signature" className="w-full h-full object-contain p-1" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                  <ExternalLink className="h-3 w-3 text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </button>
            ) : (
              <div className="w-16 h-8 rounded border flex items-center justify-center bg-white">
                <PenLine className="h-3 w-3 text-muted-foreground" />
              </div>
            )}
          </div>
        )}

        {/* Expand chevron */}
        {hasDetails && (
          <ChevronDown className={cn(
            'h-4 w-4 text-muted-foreground transition-transform shrink-0',
            expanded && 'rotate-180',
          )} />
        )}
      </div>

      {/* Expanded details */}
      {expanded && isCompleted && (
        <div className="ml-8 mt-2 text-sm space-y-1">
          {item.completedAt && (
            <p className="text-muted-foreground">
              Completed {new Date(item.completedAt).toLocaleString('en-CA')}
              {item.completedByName && ` by ${item.completedByName}`}
            </p>
          )}
          {item.notes && (
            <p className="text-muted-foreground italic">Note: {item.notes}</p>
          )}
          {/* Larger photo in expanded view */}
          {hasPhoto && fileUrl && (
            <button
              onClick={handleViewImage}
              className="group relative block w-32 h-24 rounded-lg overflow-hidden border hover:border-primary transition-colors mt-1"
            >
              <img src={fileUrl} alt={item.displayName} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <ExternalLink className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
              </div>
            </button>
          )}
          {/* Larger signature in expanded view */}
          {hasSignature && fileUrl && (
            <button
              onClick={handleViewImage}
              className="group relative block w-48 h-20 rounded-lg overflow-hidden border bg-white hover:border-primary transition-colors mt-1"
            >
              <img src={fileUrl} alt="Signature" className="w-full h-full object-contain p-2" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                <ExternalLink className="h-5 w-5 text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function ChecklistSection({ job }: ChecklistSectionProps) {
  const [imageViewer, setImageViewer] = useState<ImageViewerState>({
    open: false,
    url: null,
    title: '',
    type: 'photo',
  });

  // Cache for loaded file URLs: itemId -> signedUrl
  const [fileUrls, setFileUrls] = useState<Record<string, string>>({});
  const [loadingUrls, setLoadingUrls] = useState<Record<string, boolean>>({});

  const checklist = job.checklist || [];
  const completedCount = checklist.filter(
    (item) => item.value !== undefined && item.value !== null && item.value !== ''
  ).length;

  // Sensors with checklists
  const sensorsWithChecklist = (job.sensors || []).filter(
    (s) => s.checklist && s.checklist.length > 0,
  );

  // Collect all checklist items (job-level + sensor-level) for file URL loading
  const allChecklistItems = [
    ...checklist,
    ...sensorsWithChecklist.flatMap((s) => s.checklist ?? []),
  ];

  // Load file URLs for photo/signature items
  useEffect(() => {
    const itemsWithFiles = allChecklistItems.filter(
      (item) =>
        (item.type === 'photo' && item.photoFileId) ||
        (item.type === 'signature' && item.signatureFileId)
    );

    itemsWithFiles.forEach(async (item) => {
      const fileId = item.photoFileId || item.signatureFileId;
      if (!fileId || fileUrls[item.id] || loadingUrls[item.id]) return;

      setLoadingUrls((prev) => ({ ...prev, [item.id]: true }));

      try {
        const url = await getChecklistFileUrl(job.id, fileId);
        setFileUrls((prev) => ({ ...prev, [item.id]: url }));
      } catch (error) {
        console.error('Failed to load file URL for item:', item.id, error);
      } finally {
        setLoadingUrls((prev) => ({ ...prev, [item.id]: false }));
      }
    });
  }, [job.id, allChecklistItems, fileUrls, loadingUrls]);

  const handleViewImage = (url: string, title: string, type: 'photo' | 'signature') => {
    setImageViewer({ open: true, url, title, type });
  };

  const handleCloseImageViewer = (open: boolean) => {
    if (!open) {
      setImageViewer((prev) => ({ ...prev, open: false }));
    }
  };

  // Build sections: job-level + per-sensor
  const sections = useMemo(() => {
    const result: Array<{
      id: string;
      label: string;
      icon: 'job' | 'sensor';
      items: ChecklistItem[];
      completedCount: number;
    }> = [];

    if (checklist.length > 0) {
      result.push({
        id: 'job',
        label: 'Installation Checklist',
        icon: 'job',
        items: [...checklist].sort((a, b) => a.sortOrder - b.sortOrder),
        completedCount,
      });
    }

    for (const sensor of sensorsWithChecklist) {
      const items = sensor.checklist!;
      const completed = items.filter(
        (item) => item.value !== undefined && item.value !== null && item.value !== '',
      ).length;
      result.push({
        id: sensor.mongoSensorId,
        label: sensor.sensorName,
        icon: 'sensor',
        items: [...items].sort((a, b) => a.sortOrder - b.sortOrder),
        completedCount: completed,
      });
    }

    return result;
  }, [checklist, completedCount, sensorsWithChecklist]);

  const [activeSection, setActiveSection] = useState(sections[0]?.id ?? 'job');
  const current = sections.find((s) => s.id === activeSection) ?? sections[0];
  const currentProgress = current ? (current.items.length > 0 ? (current.completedCount / current.items.length) * 100 : 0) : 0;

  const hasSections = sections.length > 1;

  return (
    <>
      {/* Pill bar — only show if there are multiple sections */}
      {hasSections && (
        <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
          {sections.map((section) => {
            const isActive = section.id === activeSection;
            const allDone = section.completedCount === section.items.length;
            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover:bg-muted',
                )}
              >
                {section.icon === 'sensor' ? (
                  <Cpu className="h-3 w-3" />
                ) : (
                  <ClipboardList className="h-3 w-3" />
                )}
                {section.label}
                <span className={cn(
                  'ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold',
                  isActive
                    ? allDone ? 'bg-green-500/20 text-green-100' : 'bg-white/20 text-white'
                    : allDone ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground',
                )}>
                  {section.completedCount}/{section.items.length}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Active section content */}
      {current ? (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {current.icon === 'sensor' && <Cpu className="h-4 w-4" />}
                  {current.label}
                </CardTitle>
                <CardDescription>
                  {current.completedCount} of {current.items.length} items completed
                </CardDescription>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold">{Math.round(currentProgress)}%</p>
                <p className="text-xs text-muted-foreground">Complete</p>
              </div>
            </div>
            <Progress value={currentProgress} className="h-2 mt-2" />
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {current.items.map((item) => (
                <ChecklistItemRow
                  key={item.id}
                  item={item}
                  fileUrl={fileUrls[item.id] || null}
                  loadingUrl={loadingUrls[item.id] || false}
                  onViewImage={handleViewImage}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-8">
            <p className="text-muted-foreground text-center">
              No checklist items defined for this job.
            </p>
          </CardContent>
        </Card>
      )}

      <ImageViewerDialog
        open={imageViewer.open}
        onOpenChange={handleCloseImageViewer}
        imageUrl={imageViewer.url}
        title={imageViewer.title}
        type={imageViewer.type}
      />
    </>
  );
}
